import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckSquare, Shield, LogOut, ArrowRight, Settings, Menu, X, Layers, MessageSquare, Megaphone, BarChart3, History } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoImg from '@/assets/Sthitih_Logo.png';
import { useAuthStore } from '@/stores/authStore';

export const UnifiedDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-pageBg text-charcoal flex flex-col">
      {/* Header */}
      <header className="h-20 bg-cardBg border-b border-cardBorder px-4 sm:px-8 flex items-center justify-between shadow-earth-sm sticky top-0 z-30">
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Mobile 3-lines menu button */}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="md:hidden p-2 rounded-xl bg-warmSand border border-softBorder text-charcoal hover:bg-[#EFE5D6] transition-colors cursor-pointer"
            aria-label="Open Navigation Menu"
          >
            <Menu className="w-5 h-5 text-forest" />
          </button>

          <div className="bg-white border border-softBorder p-1.5 sm:p-2 rounded-xl">
            <img src={logoImg} alt="Svavas Logo" className="h-8 sm:h-10 w-auto object-contain" />
          </div>
          <div>
            <h1 className="text-base sm:text-xl font-extrabold text-charcoal leading-tight">Svavas Super Admin Portal</h1>
            <p className="text-[10px] sm:text-xs font-semibold text-mutedSubheading uppercase tracking-wider">
              Unified Platform Management
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          {/* Direct Settings Button on Image 2 Header */}
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl bg-warmSand hover:bg-[#EFE5D6] text-forest border border-softBorder font-bold text-xs transition-colors cursor-pointer shadow-xs"
            title="Platform Settings & Security"
          >
            <Settings className="w-4 h-4 text-forest" />
            <span className="hidden sm:inline">Settings</span>
          </button>

          <div className="hidden sm:flex items-center gap-3 bg-warmSand px-4 py-2 rounded-xl border border-softBorder">
            <div className="w-8 h-8 rounded-lg bg-forest text-white flex items-center justify-center font-bold text-xs">
              SA
            </div>
            <div>
              <p className="text-xs font-extrabold text-charcoal">Super Admin</p>
              <p className="text-[10px] font-semibold text-mutedBody">{user?.email || 'superadmin@svavas.com'}</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="p-2.5 rounded-xl bg-btn-secBg hover:bg-[#FCE8E8] text-mutedSubheading hover:text-[#D64545] border border-btn-secBorder transition-colors cursor-pointer"
            title="Sign Out"
          >
            <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </header>

      {/* Hero / Portal Selection */}
      <main className="flex-1 max-w-6xl w-full mx-auto p-4 sm:p-8 flex flex-col justify-center">
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-warmSand rounded-full text-xs font-bold text-forest mb-4 border border-softBorder">
            <Shield className="w-4 h-4" /> Select Administration Console
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-charcoal tracking-tight">Welcome to Super Admin Dashboard</h2>
          <p className="text-xs sm:text-sm font-medium text-mutedSubheading mt-2 max-w-lg mx-auto leading-relaxed">
            Access and manage multi-tenant workspaces, platform reports, support tickets, and enterprise requests across both platforms.
          </p>
        </div>

        {/* 3 Portal Cards: Event Management, Task Management, Platform Settings & Security */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto w-full">
          {/* Event Management Portal Card */}
          <div
            onClick={() => navigate('/events')}
            className="group card-earth p-6 sm:p-8 cursor-pointer hover:border-forest hover:shadow-earth-lg transition-all duration-300 relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 right-0 w-28 h-28 bg-forest/5 rounded-bl-full group-hover:bg-forest/10 transition-all" />

            <div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-forest/10 text-forest flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Calendar className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <span className="text-xs font-extrabold uppercase tracking-wider text-walnut">Module 01</span>
              <h3 className="text-xl sm:text-2xl font-black text-charcoal mt-1 group-hover:text-forest transition-colors">
                Event Management
              </h3>
              <p className="text-xs font-medium text-mutedBody mt-3 leading-relaxed">
                Manage client workspaces, subscription plans, platform reports, system announcements, audit logs, and support tickets.
              </p>
            </div>

            <div className="mt-8 pt-5 border-t border-cardBorder flex items-center justify-between text-forest font-bold text-xs">
              <span>Open Event Console</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>

          {/* Task Management Portal Card */}
          <div
            onClick={() => navigate('/tasks')}
            className="group card-earth p-6 sm:p-8 cursor-pointer hover:border-walnut hover:shadow-earth-lg transition-all duration-300 relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 right-0 w-28 h-28 bg-walnut/5 rounded-bl-full group-hover:bg-walnut/10 transition-all" />

            <div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-walnut/10 text-walnut flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <CheckSquare className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <span className="text-xs font-extrabold uppercase tracking-wider text-forest">Module 02</span>
              <h3 className="text-xl sm:text-2xl font-black text-charcoal mt-1 group-hover:text-walnut transition-colors">
                Task Management
              </h3>
              <p className="text-xs font-medium text-mutedBody mt-3 leading-relaxed">
                Manage enterprise quote requests, billing invoices, user provisioning, and platform registered users.
              </p>
            </div>

            <div className="mt-8 pt-5 border-t border-cardBorder flex items-center justify-between text-walnut font-bold text-xs">
              <span>Open Task Console</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>

          {/* Platform Settings & Security Card (DIRECT ON IMAGE 2 PAGE) */}
          <div
            onClick={() => navigate('/settings')}
            className="group card-earth p-6 sm:p-8 cursor-pointer hover:border-[#1F5A43] hover:shadow-earth-lg transition-all duration-300 relative overflow-hidden flex flex-col justify-between bg-[#FFFDF9]"
          >
            <div className="absolute top-0 right-0 w-28 h-28 bg-[#8A4B22]/5 rounded-bl-full group-hover:bg-[#8A4B22]/10 transition-all" />

            <div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#8A4B22]/10 text-[#8A4B22] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Settings className="w-6 h-6 sm:w-7 sm:h-7 text-[#8A4B22]" />
              </div>
              <span className="text-xs font-extrabold uppercase tracking-wider text-[#8A4B22]">Security & Limits</span>
              <h3 className="text-xl sm:text-2xl font-black text-charcoal mt-1 group-hover:text-[#1F5A43] transition-colors">
                Platform Settings
              </h3>
              <p className="text-xs font-medium text-mutedBody mt-3 leading-relaxed">
                Manage subscription tier plans, security credentials, active login device sessions, and global platform parameters.
              </p>
            </div>

            <div className="mt-8 pt-5 border-t border-cardBorder flex items-center justify-between text-[#8A4B22] font-bold text-xs group-hover:text-[#1F5A43]">
              <span>Open Settings & Sessions</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-cardBorder text-center text-xs font-semibold text-mutedBrown">
        © 2026 Svavas Platforms. All rights reserved.
      </footer>

      {/* Slide-out Mobile Side Drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <div className="fixed inset-0 z-[1200] md:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-xs"
            />

            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 280 }}
              className="absolute top-0 left-0 bottom-0 w-[84%] max-w-sm bg-[#FFFDF9] border-r border-[#E4D8C8] flex flex-col justify-between shadow-2xl overflow-y-auto"
            >
              <div>
                <div className="p-5 border-b border-[#E4D8C8] flex items-center justify-between bg-[#F7F2EA]">
                  <div className="flex items-center gap-3">
                    <img src={logoImg} alt="Sthitih" className="h-8 w-auto object-contain" />
                    <div>
                      <h2 className="text-sm font-extrabold text-[#243238]">Svavas Super Admin</h2>
                      <p className="text-[10px] font-bold text-[#8A4B22] uppercase tracking-wider">Navigation Menu</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setDrawerOpen(false)}
                    className="p-1.5 rounded-xl hover:bg-[#EFE5D6] text-[#6F706C] transition-colors cursor-pointer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-4 mx-4 my-4 rounded-2xl bg-[#F7F2EA] border border-[#DDD0BE] flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#1F5A43] text-white flex items-center justify-center font-extrabold text-sm shadow-xs">
                    SA
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-black text-[#243238] truncate">Super Administrator</p>
                    <p className="text-[10px] text-[#6F706C] font-semibold truncate mt-0.5">{user?.email || 'superadmin@svavas.com'}</p>
                  </div>
                </div>

                <div className="px-4 space-y-1.5 pb-4">
                  <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-widest px-3 block mb-1">Administration Consoles</span>
                  <button
                    onClick={() => { setDrawerOpen(false); navigate('/events'); }}
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold text-[#243238] hover:bg-[#F7F2EA] hover:text-[#1F5A43] transition-all text-left cursor-pointer"
                  >
                    <Calendar className="w-4 h-4 text-[#1F5A43]" />
                    <span>Module 01: Event Management</span>
                  </button>
                  <button
                    onClick={() => { setDrawerOpen(false); navigate('/tasks'); }}
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold text-[#243238] hover:bg-[#F7F2EA] hover:text-[#1F5A43] transition-all text-left cursor-pointer"
                  >
                    <CheckSquare className="w-4 h-4 text-[#8A4B22]" />
                    <span>Module 02: Task Management</span>
                  </button>
                  <button
                    onClick={() => { setDrawerOpen(false); navigate('/settings'); }}
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold bg-[#1F5A43] text-white shadow-xs transition-all text-left cursor-pointer"
                  >
                    <Settings className="w-4 h-4 text-white" />
                    <span>Platform Settings & Security</span>
                  </button>

                  <div className="border-t border-[#E4D8C8] my-3" />

                  <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-widest px-3 block mb-1">Quick Links</span>
                  <button
                    onClick={() => { setDrawerOpen(false); navigate('/events/global-ps'); }}
                    className="w-full flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-semibold text-[#243238] hover:bg-[#F7F2EA] transition-all text-left cursor-pointer"
                  >
                    <Layers className="w-4 h-4 text-[#8D8A83]" />
                    <span>PS Repositories</span>
                  </button>
                  <button
                    onClick={() => { setDrawerOpen(false); navigate('/events/reports'); }}
                    className="w-full flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-semibold text-[#243238] hover:bg-[#F7F2EA] transition-all text-left cursor-pointer"
                  >
                    <BarChart3 className="w-4 h-4 text-[#8D8A83]" />
                    <span>Reports & Analytics</span>
                  </button>
                  <button
                    onClick={() => { setDrawerOpen(false); navigate('/events/audit-logs'); }}
                    className="w-full flex items-center gap-3 px-3.5 py-2 rounded-xl text-xs font-semibold text-[#243238] hover:bg-[#F7F2EA] transition-all text-left cursor-pointer"
                  >
                    <History className="w-4 h-4 text-[#8D8A83]" />
                    <span>Audit Logs</span>
                  </button>
                </div>
              </div>

              <div className="p-4 border-t border-[#E4D8C8] bg-[#F7F2EA]">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-[#D64545] hover:bg-rose-100 rounded-xl transition-colors cursor-pointer"
                >
                  <LogOut className="w-4 h-4 text-[#D64545]" />
                  <span>Sign Out</span>
                </button>
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
